/**
 * The Tools panel's Add participant section (web): ring a Deaf participant
 * into this call with conference_add, by VRS number, @handle or email. Each
 * row says only what the server confirmed (groupInvite.ts): sending, ringing,
 * accepted, declined, no answer, or why it failed.
 */
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { INVITE_CONFIRM_TIMEOUT_MS, inviteRowKey, inviteRowsReducer, inviteStatusText } from '../../../vrs-layout/groupInvite';
import { inviteDialCountry } from '../../../vrs-layout/inviteDialCountry';
import { addParticipantTarget } from '../../addParticipant';
const useStyles = makeStyles()(theme => {
    return {
        row: {
            display: 'flex',
            gap: 8
        },
        input: {
            background: theme.palette.ui02,
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: 8,
            boxSizing: 'border-box',
            color: theme.palette.text01,
            flex: 1,
            fontSize: 15,
            minHeight: 48,
            minWidth: 0,
            padding: '0 12px',
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 1
            }
        },
        ringIn: {
            background: theme.palette.action01,
            border: 0,
            borderRadius: 8,
            color: theme.palette.text01,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 700,
            minHeight: 48,
            padding: '0 14px',
            whiteSpace: 'nowrap',
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 2
            }
        },
        error: {
            color: theme.palette.textError,
            fontSize: 14,
            lineHeight: '20px',
            marginTop: 6
        },
        list: {
            listStyle: 'none',
            margin: '8px 0 0',
            padding: 0
        },
        item: {
            display: 'flex',
            fontSize: 14,
            gap: 8,
            justifyContent: 'space-between',
            lineHeight: '20px',
            padding: '4px 0'
        },
        name: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        state: {
            color: theme.palette.text02,
            flexShrink: 0
        }
    };
});
/**
 * Ring a Deaf participant into this call.
 *
 * @param {Object} props - The props.
 * @param {string} props.callId - This room's queue call.
 * @returns {ReactElement}
 */
export default function AddParticipantSection({ callId }) {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const [typed, setTyped] = useState('');
    const [invalid, setInvalid] = useState(false);
    const [rows, update] = useReducer(inviteRowsReducer, []);
    const timers = useRef([]);
    useEffect(() => {
        update({ type: 'reset' });
        const onSent = (data) => {
            if (data?.callId === callId) {
                const target = data.target;
                const resolved = target && typeof target === 'object' ? target.name : undefined;
                update({
                    type: 'sent',
                    inviteId: data.inviteId,
                    name: typeof resolved === 'string' ? resolved : undefined,
                    requested: data.requested,
                    ringing: data.ringing,
                    target: data.target
                });
            }
        };
        const onResult = (data) => {
            if (data?.callId === callId) {
                update({
                    type: 'result',
                    inviteId: data.inviteId,
                    name: data.name || undefined,
                    status: data.status,
                    targetId: data.targetId
                });
            }
        };
        const onError = (data) => {
            if (data?.operation === 'conference_add' && (!data.callId || String(data.callId) === callId)) {
                update({
                    type: 'error',
                    code: data.code
                });
            }
        };
        queueService.on('conferenceInviteSent', onSent);
        queueService.on('conferenceInviteResult', onResult);
        queueService.on('error', onError);
        return () => {
            queueService.off('conferenceInviteSent', onSent);
            queueService.off('conferenceInviteResult', onResult);
            queueService.off('error', onError);
            timers.current.forEach(clearTimeout);
            timers.current = [];
        };
    }, [callId]);
    const ringIn = useCallback(() => {
        const target = addParticipantTarget(typed, inviteDialCountry());
        if (!target) {
            setInvalid(true);
            return;
        }
        const key = inviteRowKey(target);
        setInvalid(false);
        update({
            type: 'sending',
            label: typed.trim(),
            target
        });
        queueService.conferenceAdd(callId, target);
        timers.current.push(setTimeout(() => update({
            type: 'unconfirmed',
            key
        }), INVITE_CONFIRM_TIMEOUT_MS));
        setTyped('');
    }, [callId, typed]);
    const onChange = useCallback((event) => {
        setTyped(event.target.value);
        setInvalid(false);
    }, []);
    const onKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            ringIn();
        }
    }, [ringIn]);
    return (React.createElement("div", null,
        React.createElement("div", { className: classes.row },
            React.createElement("input", { "aria-invalid": invalid, "aria-label": t('vrs.tools.deafParticipant'), className: classes.input, maxLength: 120, onChange: onChange, onKeyDown: onKeyDown, placeholder: t('vrs.tools.deafParticipantPlaceholder'), type: 'text', value: typed }),
            React.createElement("button", { className: classes.ringIn, disabled: !typed.trim(), onClick: ringIn, type: 'button' }, t('vrs.tools.ringIn'))),
        invalid && React.createElement("div", { className: classes.error, role: 'alert' }, t('vrs.tools.participantInvalid')),
        rows.length > 0 && (React.createElement("ul", { "aria-live": 'polite', className: classes.list }, rows.map(row => (React.createElement("li", { className: classes.item, key: row.key },
            React.createElement("span", { className: classes.name }, row.label),
            React.createElement("span", { className: classes.state }, inviteStatusText(row, t)))))))));
}
